import React, { useState, useEffect, memo } from "react";
import { employeeService, authService } from "../services/api";
import EmployeeProfileDetails from "../components/Admin/create-employee/EmployeeProfileDetails";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const EmployeeProfile = memo(function EmployeeProfile() {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const navigate = useNavigate();
  // Function to fetch employee data
  // Uses cascading fallback methods - exits early when any method succeeds
  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      console.log("Fetching employee data...");

      // Method 1: Try to get current user data first
      try {
        const currentUserResponse = await authService.getCurrentUser();
        console.log("Current user response:", currentUserResponse);

        if (currentUserResponse && currentUserResponse.data) {
          const userData = currentUserResponse.data;
          console.log("User data:", userData);

          // If user has an employee ID, fetch full employee details
          if (userData.employeeId || userData.id) {
            const employeeId = userData.employeeId || userData.id;
            console.log("Fetching employee details for ID:", employeeId);

            const employeeResponse = await employeeService.getEmployee(
              employeeId
            );
            console.log("Employee response:", employeeResponse);

            if (employeeResponse && employeeResponse.data) {
              console.log("Setting employee data:", employeeResponse.data);
              setEmployee(employeeResponse.data);
              // toast.success('Employee profile loaded successfully!', { id: 'employee-profile-success' });
              return; // Success, exit early - no need to try other methods
            }
          } else {
            // If no employee ID, use user data as employee data
            console.log("Using user data as employee data");
            setEmployee(userData);
            toast.success("User data loaded successfully!", {
              id: "user-data-success",
            });
            return; // Success, exit early - no need to try other methods
          }
        }
      } catch (userError) {
        console.log("Method 1 failed, trying fallback method:", userError);
        // Don't show error toast here - Method 2 might succeed
      }

      // Method 2: Fallback - try to get employee by stored user ID (only if Method 1 failed)
      const currentUserId = localStorage.getItem("userId");
      console.log("Fallback: trying with stored userId:", currentUserId);

      if (currentUserId) {
        try {
          const response = await employeeService.getEmployee(currentUserId);
          console.log("Fallback employee response:", response);

          if (response && response.data) {
            console.log("Setting employee data from fallback:", response.data);
            setEmployee(response.data);
            // toast.success('Employee profile loaded via fallback!', { id: 'fallback-success' });
            return; // Success, exit early - no need to try other methods
          }
        } catch (fallbackError) {
          console.log("Method 2 also failed:", fallbackError);
          // Don't show error toast here - Method 3 will handle the final error
        }
      }

      // Method 3: If all methods fail, set empty data (only if Methods 1 & 2 failed)
      console.log("All methods failed, setting empty employee data");
      toast.error("Unable to load employee data. Please try refreshing.", {
        id: "load-error",
      });
      setEmployee({
        empCode: "",
        name: "",
        gender: "",
        dateOfBirth: "",
        branch: "",
        designation: "",
        department: "",
        subDepartment: "",
        grade: "",
        category: "",
        reportingManager: "",
        employeeType: "",
        employmentStatus: "",
        dateOfJoin: "",
        mobileNumber: "",
        personalEmail: "",
        officialEmail: "",
        bloodGroup: "",
        aadhaarNo: "",
        pan: "",
        address: "",
        emergencyContact: {
          name: "",
          number: "",
          relationship: "",
        },
        familyMembers: [],
        bankName: "",
        bankAccountNo: "",
        ifscCode: "",
        qualifications: [],
        experiences: [],
      });
    } catch (error) {
      console.error("Failed to fetch employee data:", error);
      toast.error("An unexpected error occurred while loading employee data", {
        id: "unexpected-error",
      });
      // Set empty data as fallback
      setEmployee({
        empCode: "",
        name: "",
        gender: "",
        dateOfBirth: "",
        branch: "",
        designation: "",
        department: "",
        subDepartment: "",
        grade: "",
        category: "",
        reportingManager: "",
        employeeType: "",
        employmentStatus: "",
        dateOfJoin: "",
        mobileNumber: "",
        personalEmail: "",
        officialEmail: "",
        bloodGroup: "",
        aadhaarNo: "",
        pan: "",
        address: "",
        emergencyContact: {
          name: "",
          number: "",
          relationship: "",
        },
        familyMembers: [],
        bankName: "",
        bankAccountNo: "",
        ifscCode: "",
        qualifications: [],
        experiences: [],
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh employee data
  const refreshEmployeeData = async () => {
    console.log("Refreshing employee data...");
    toast.loading("Refreshing employee data...", { id: "refresh-loading" });
    await fetchEmployeeData();
  };

  useEffect(() => {
    console.log("EmployeeProfile useEffect triggered - fetching data");
    fetchEmployeeData();
  }, []);

  // Debug: Log employee data when it changes
  useEffect(() => {
    console.log("Employee data updated:", employee);
  }, [employee]);

  const handleCancel = () => {
    // Reset form data to original employee data
    // setEmployee(employee);
    navigate("/employee/home");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full">
      <EmployeeProfileDetails
        employee={employee}
        darkMode={darkMode}
        onCancel={handleCancel}
        isEmployeeView={true}
      />
    </div>
  );
});

export default EmployeeProfile;
